// App.js
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, BrowserRouter as Router } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Snackbar,
  IconButton,
  LinearProgress,
  Box,
} from '@mui/material';
import axios from 'axios';
import { ContentCopy } from '@mui/icons-material';

function App() {
  return (
    <Router basename="/passgenpro">
      <Container>
        <Box
          display="flex"
          alignItems="center"
          className="header"
          style={{ marginTop: '16px' }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/passgen_logo.png`}
            alt="PassGenPro Logo"
            className="logo"
          />
          <Typography variant="h3" gutterBottom className="header-text">
            PassGenPro
          </Typography>
        </Box>
        <Typography
          variant="body1"
          gutterBottom
          style={{ marginBottom: '10px' }}
        >
          Generate secure passwords with ease.
        </Typography>
        <Routes>
          <Route path="/" element={<PasswordGenerator />} />
        </Routes>
      </Container>
    </Router>
  );
}

function PasswordGenerator() {
  const [length, setLength] = useState(12);
  const [uppercase, setUppercase] = useState(true);
  const [lowercase, setLowercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [special, setSpecial] = useState(true);
  const [password, setPassword] = useState('');
  const [open, setOpen] = useState(false);
  const [strength, setStrength] = useState(0);

  const evaluateStrength = useCallback(() => {
    let strength = 0;

    // Check length conditions
    if (length >= 8) strength++;
    if (length >= 12) strength++;
    if (length >= 16) strength++;

    // Check character types
    const hasUppercase = uppercase;
    const hasLowercase = lowercase;
    const hasNumbers = numbers;
    const hasSpecial = special;

    // Strength conditions
    if (hasUppercase || hasLowercase) strength++;
    if (hasNumbers || hasSpecial) strength++;

    // Check for very strong condition
    if (
      hasUppercase &&
      hasLowercase &&
      hasNumbers &&
      hasSpecial &&
      length >= 16
    ) {
      strength = 5;
    }

    setStrength(strength);
  }, [length, uppercase, lowercase, numbers, special]);

  useEffect(() => {
    evaluateStrength();
  }, [length, uppercase, lowercase, numbers, special, evaluateStrength]);

  const generatePassword = async () => {
    try {
      const response = await axios.post(
        'https://www.wisemancodes.com/passgenpro/api/generate-password',
        {
          length: parseInt(length),
          uppercase,
          lowercase,
          numbers,
          special,
        }
      );
      setPassword(response.data.password);
    } catch (error) {
      console.error('Error generating password:', error);
    }
  };

  const resetDefaults = () => {
    setLength(12);
    setUppercase(true);
    setLowercase(true);
    setNumbers(true);
    setSpecial(true);
    setPassword('');
  };

  const getStrengthLabel = () => {
    switch (strength) {
      case 0:
      case 1:
        return 'Weak';
      case 2:
      case 3:
        return 'Moderate';
      case 4:
        return 'Strong';
      case 5:
        return 'Very Strong';
      default:
        return '';
    }
  };

  const getProgressColor = () => {
    switch (strength) {
      case 0:
      case 1:
        return 'red';
      case 2:
      case 3:
        return 'yellow';
      case 4:
        return 'lightgreen';
      case 5:
        return 'darkgreen';
      default:
        return 'gray';
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Password Length"
            type="number"
            value={length}
            onChange={(e) =>
              setLength(Math.max(1, Math.min(32, parseInt(e.target.value))))
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={uppercase}
                  onChange={(e) => setUppercase(e.target.checked)}
                />
              }
              label="Uppercase Letters"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={lowercase}
                  onChange={(e) => setLowercase(e.target.checked)}
                />
              }
              label="Lowercase Letters"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={numbers}
                  onChange={(e) => setNumbers(e.target.checked)}
                />
              }
              label="Numbers"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={special}
                  onChange={(e) => setSpecial(e.target.checked)}
                />
              }
              label="Special Characters"
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: '2px' }}>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={generatePassword}
          >
            Generate
          </Button>
        </Grid>
        <Grid item>
          <Button variant="contained" color="secondary" onClick={resetDefaults}>
            Reset
          </Button>
        </Grid>
      </Grid>
      <Typography variant="body1" style={{ marginTop: '16px' }}>
        Generated Password:
      </Typography>
      <Typography variant="h6" style={{ wordBreak: 'break-all' }}>
        {password}
      </Typography>
      {password && (
        <Button
          variant="outlined"
          startIcon={<ContentCopy />}
          onClick={handleCopy}
          size="small"
          style={{ marginTop: '8px' }}
        >
          Copy
        </Button>
      )}
      <Box sx={{ width: '100%', mt: 4 }}>
        <Typography variant="body1">Strength: {getStrengthLabel()}</Typography>
        <LinearProgress
          variant="determinate"
          value={(strength / 5) * 100}
          sx={{
            backgroundColor: 'lightgray',
            '& .MuiLinearProgress-bar': { backgroundColor: getProgressColor() },
          }}
        />
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={handleClose}
        message="Password copied to clipboard"
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
          >
            x
          </IconButton>
        }
      />
    </div>
  );
}

export default App;
